import { useEffect } from 'react';
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { error as errorLog, info } from '@/utils/logging';

export interface LoggingOptions {
  logSuccess?: boolean;
  logError?: boolean;
  logSettled?: boolean;
  context?: Record<string, any>;
}

export function useQueryWithLogging<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  loggingOptions: LoggingOptions = {},
): UseQueryResult<TData, TError> {
  const {
    logSuccess = true,
    logError = true,
    logSettled = false,
    context = {},
  } = loggingOptions;

  const queryKey = options.queryKey;
  const queryKeyString = Array.isArray(queryKey)
    ? queryKey.join(':')
    : String(queryKey);

  const result = useQuery(options);
  const { data, error, status } = result;

  // Use useEffect to log based on query state changes
  useEffect(() => {
    if (logSuccess && result.isSuccess && result.data !== undefined) {
      info(null, `Query succeeded: ${queryKeyString}`, {
        queryKey: queryKey,
        dataLength: Array.isArray(data) ? data.length : undefined,
        hasData: !!data,
        ...context,
      });
    }

    if (logError && result.isError && result.error) {
      errorLog(result.error, `Query failed: ${queryKeyString}`, {
        queryKey: queryKey,
        errorMessage: error instanceof Error ? error.message : String(error),
        ...context,
      });
    }

    if (logSettled && (result.isSuccess || result.isError)) {
      info(null, `Query settled: ${queryKeyString}`, {
        queryKey: queryKey,
        hasData: !!data,
        hasError: !!error,
        status: status,
        ...context,
      });
    }
  }, [
    result.isSuccess,
    result.isError,
    result.data,
    result.error,
    result.status,
    queryKeyString,
    logSuccess,
    logError,
    logSettled,
  ]);

  return result;
}
